import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { supabase } from '../lib/supabase';
import { useAuth } from '../auth/AuthProvider';
import { DISPLAY_FONT, makeThemedStyles, radii, useTheme } from '../v10/theme';
import { Atmos, Field, GlassCard, GoldButton } from '../v10/ui';
import type { ToolProps } from '../olmps/ToolHost';
import { ScreenHelp } from '../v10/ScreenHelp';
import { sofiaHelp } from '../olmps/help/sofiaHelp';

// ============================================================================
// FULL-FIDELITY native replica of the web REACH per-project editor.
// Schema verified against:
//   - src/app/reach/[projectId]/page.tsx (editor: every field, save shape)
//   - src/app/design/page.tsx            (handleAddProject('REACH') insert payload)
//   - src/interfaces/settings.ts         (ReachDocument / ReachStep)
//
// Mobile is single-panel: LIST -> DETAIL (same pattern as TkneTool.tsx for
// designs), rather than web's route-per-project navigation.
//
// Column names are snake_case (Postgres); this file talks to the `reaches`
// table directly so web and mobile can edit the same row safely.
//
// EXCLUSIONS (mirrors TkneTool.tsx's documented exclusions for the same
// reasons — these embed separate, unrelated top-level features):
//   1. Inline Task-list CRUD (Task section "Add as List" / "Add as Cat" +
//      embedded task list content) — this embeds the separate Tasks feature
//      (its own tables + screens). Replaced with a read-only status line;
//      `sync_list_id` / `sync_category_list_id` / `sync_category_name` still
//      round-trip untouched so web's Task section keeps working.
//   2. Advanced view (`viewMode: 'advanced'`) — web's REACH page only ever
//      writes/reads the Basic-mode fields in its save payload (projectTitle,
//      steps[].what, basicSteps, sync*); this tool mirrors that same surface
//      1:1 (the ReachStep's `how`/`why` fields exist on the type but the web
//      editor's Basic table only ever renders/edits `what`, so this does too).
// ============================================================================

const REACH_TABLE = 'reaches';

interface BasicStep {
  id: string;
  text: string;
  checked: boolean;
}

interface ReachStep {
  id: string;
  label: string;
  checked: boolean;
  what: string;
  how: string;
  why: string;
}

const DEFAULT_STEP_PROMPTS: Record<string, string> = {
  Research: 'What do I need to learn or find out about...',
  Evaluation: 'What I thought of what I found was...',
  Analysis: 'The patterns and insights I see are...',
  Contribution: 'What I am adding or building on top is...',
  Hindsight: 'Next time I know...',
};

const DEFAULT_REACH_LABELS = ['Research', 'Evaluation', 'Analysis', 'Contribution', 'Hindsight'];

interface ReachRow {
  id: string;
  project_id: string;
  user_id: string;
  design_group_id: string | null;
  is_active: boolean;
  view_mode: 'basic' | 'advanced';
  section_toggles: Record<string, boolean>;
  project_title: string;
  steps: ReachStep[];
  basic_steps: BasicStep[];
  sync_list_id: string;
  sync_category_list_id: string;
  sync_category_name: string;
  order: number | null;
  created_at: string;
  updated_at: string;
}

function generateId(): string {
  return `d-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`;
}

function uuid(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function nowIso(): string {
  return new Date().toISOString();
}

function asArray<T>(v: unknown): T[] {
  return Array.isArray(v) ? (v as T[]) : [];
}

function ensureDefaultBasicSteps(): BasicStep[] {
  return Array.from({ length: 3 }, () => ({ id: generateId(), text: '', checked: false }));
}

function defaultReachSteps(): ReachStep[] {
  return DEFAULT_REACH_LABELS.map((label) => ({ id: generateId(), label, checked: false, what: '', how: '', why: '' }));
}

/** Matches the insert payload built by web's design/page.tsx handleAddProject('REACH', ...). */
function buildCreatePayload(userId: string) {
  const id = uuid();
  const now = nowIso();
  const steps = defaultReachSteps();
  return {
    id,
    project_id: id,
    user_id: userId,
    design_group_id: null,
    is_active: true,
    view_mode: 'basic' as const,
    section_toggles: Object.fromEntries(DEFAULT_REACH_LABELS.map((l) => [l.toLowerCase(), true])),
    project_title: '',
    steps,
    created_at: now,
    updated_at: now,
  };
}

function normalizeRow(raw: any): ReachRow {
  const steps = asArray<any>(raw.steps).map((s) => ({
    id: s?.id ?? generateId(),
    label: s?.label ?? '',
    checked: !!s?.checked,
    what: s?.what ?? '',
    how: s?.how ?? '',
    why: s?.why ?? '',
  }));
  return {
    id: raw.id,
    project_id: raw.project_id,
    user_id: raw.user_id,
    design_group_id: raw.design_group_id ?? null,
    is_active: raw.is_active ?? true,
    view_mode: raw.view_mode === 'advanced' ? 'advanced' : 'basic',
    section_toggles: raw.section_toggles && typeof raw.section_toggles === 'object' ? raw.section_toggles : {},
    project_title: raw.project_title ?? '',
    steps: steps.length ? steps : defaultReachSteps(),
    basic_steps: asArray<BasicStep>(raw.basic_steps),
    sync_list_id: raw.sync_list_id ?? '',
    sync_category_list_id: raw.sync_category_list_id ?? '',
    sync_category_name: raw.sync_category_name ?? '',
    order: typeof raw.order === 'number' ? raw.order : null,
    created_at: raw.created_at,
    updated_at: raw.updated_at,
  };
}

/** Matches web's reachToRow() save shape exactly. */
function toUpdatePayload(row: ReachRow): Record<string, any> {
  return {
    project_title: row.project_title,
    steps: row.steps,
    basic_steps: row.basic_steps ?? [],
    sync_list_id: row.sync_list_id || '',
    sync_category_list_id: row.sync_category_list_id || '',
    sync_category_name: row.sync_category_name || '',
    updated_at: nowIso(),
  };
}

function statusLabel(row: ReachRow): string {
  const filled = row.steps.filter((s) => (s.what || '').trim().length > 0).length;
  if (row.steps.length === 0) return 'No steps yet';
  if (filled === 0) return 'Not started';
  if (filled === row.steps.length) return 'All steps filled';
  return `${filled}/${row.steps.length} steps filled`;
}

// ---------------------------------------------------------------------------
// Small reusable presentational pieces (same conventions as TkneTool.tsx)
// ---------------------------------------------------------------------------

function RemoveMark({ onPress }: { onPress: () => void }) {
  const styles = useStyles();
  return (
    <Pressable onPress={onPress} hitSlop={8} style={styles.removeButton}>
      <Text style={styles.removeMark}>×</Text>
    </Pressable>
  );
}

function SmallButton({ label, onPress }: { label: string; onPress: () => void }) {
  const styles = useStyles();
  return (
    <Pressable onPress={onPress} style={styles.smallButton} hitSlop={6}>
      <Text style={styles.smallButtonText}>{label}</Text>
    </Pressable>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function SofiaTool(_props: ToolProps) {
  const { t } = useTheme();
  const styles = useStyles();
  const { session } = useAuth();
  const userId = session?.user.id;

  const [screen, setScreen] = useState<'list' | 'detail'>('list');
  const [reaches, setReaches] = useState<ReachRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newTitle, setNewTitle] = useState('');
  const [busy, setBusy] = useState(false);

  const [row, setRow] = useState<ReachRow | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
  }, []);

  // ----- list loading -----
  const loadList = useCallback(async () => {
    if (!userId) return;
    const { data, error: err } = await supabase
      .from(REACH_TABLE)
      .select('*')
      .eq('user_id', userId)
      .order('updated_at', { ascending: false });
    if (err) {
      setError(err.message);
    } else {
      setError(null);
      setReaches((data ?? []).map(normalizeRow));
    }
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  // ----- create / delete / toggle active -----
  const createReach = async () => {
    const title = newTitle.trim();
    if (!userId || busy) return;
    setBusy(true);
    const payload = buildCreatePayload(userId);
    payload.project_title = title;
    const { data, error: err } = await supabase.from(REACH_TABLE).insert(payload).select().single();
    if (err) {
      setError(err.message);
      setBusy(false);
      return;
    }
    setNewTitle('');
    await loadList();
    setBusy(false);
    if (data) openReach(normalizeRow(data));
  };

  const deleteReach = (item: ReachRow) => {
    Alert.alert('Delete REACH project?', item.project_title || 'Untitled Reach', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete',
        style: 'destructive',
        onPress: async () => {
          setReaches((prev) => prev.filter((r) => r.id !== item.id));
          if (row?.id === item.id) {
            setRow(null);
            setScreen('list');
          }
          const { error: err } = await supabase.from(REACH_TABLE).delete().eq('id', item.id).eq('user_id', userId!);
          if (err) {
            setError(err.message);
            await loadList();
          }
        },
      },
    ]);
  };

  const toggleActive = async (item: ReachRow) => {
    setReaches((prev) => prev.map((r) => (r.id === item.id ? { ...r, is_active: !r.is_active } : r)));
    const { error: err } = await supabase
      .from(REACH_TABLE)
      .update({ is_active: !item.is_active, updated_at: nowIso() })
      .eq('id', item.id)
      .eq('user_id', userId!);
    if (err) {
      setError(err.message);
      await loadList();
    }
  };

  const openReachMenu = (item: ReachRow) => {
    Alert.alert(item.project_title || 'Untitled Reach', undefined, [
      { text: item.is_active !== false ? 'Set Inactive' : 'Set Active', onPress: () => void toggleActive(item) },
      { text: 'Delete', style: 'destructive', onPress: () => deleteReach(item) },
      { text: 'Cancel', style: 'cancel' },
    ]);
  };

  // ----- open a reach in the detail editor -----
  const openReach = async (item: ReachRow) => {
    setDetailLoading(true);
    setScreen('detail');
    const { data, error: err } = await supabase.from(REACH_TABLE).select('*').eq('id', item.id).single();
    if (err || !data) {
      setError(err?.message ?? 'Failed to load REACH project.');
      setScreen('list');
      setDetailLoading(false);
      return;
    }
    setRow(normalizeRow(data));
    setDetailLoading(false);
  };

  const goBack = async () => {
    setScreen('list');
    setRow(null);
    await loadList();
  };

  // ----- autosave (1.5s debounce, matches web) -----
  const scheduleSave = useCallback((next: ReachRow) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setIsSaving(true);
      const { error: err } = await supabase
        .from(REACH_TABLE)
        .update(toUpdatePayload(next))
        .eq('id', next.id)
        .eq('user_id', userId!);
      setIsSaving(false);
      if (err) setError(err.message);
    }, 1500);
  }, [userId]);

  const updateRow = useCallback((updater: (prev: ReachRow) => ReachRow) => {
    setRow((prev) => {
      if (!prev) return prev;
      const next = updater(prev);
      scheduleSave(next);
      return next;
    });
  }, [scheduleSave]);

  const setField = <K extends keyof ReachRow>(key: K, value: ReachRow[K]) => {
    updateRow((prev) => ({ ...prev, [key]: value }));
  };

  const updateStep = (stepId: string, patch: Partial<ReachStep>) =>
    updateRow((prev) => ({ ...prev, steps: prev.steps.map((s) => (s.id === stepId ? { ...s, ...patch } : s)) }));

  // Basic steps (BasicStepsTable equivalent)
  const addBasicStep = () =>
    updateRow((prev) => ({ ...prev, basic_steps: [...(prev.basic_steps.length ? prev.basic_steps : ensureDefaultBasicSteps()), { id: generateId(), text: '', checked: false }] }));
  const updateBasicStep = (id: string, patch: Partial<BasicStep>, current: BasicStep[]) =>
    updateRow((prev) => ({ ...prev, basic_steps: current.map((s) => (s.id === id ? { ...s, ...patch } : s)) }));
  const removeBasicStep = (id: string, current: BasicStep[]) =>
    updateRow((prev) => ({ ...prev, basic_steps: current.filter((s) => s.id !== id) }));

  // -------------------------------------------------------------------------
  // Render: LIST screen
  // -------------------------------------------------------------------------

  if (screen === 'list') {
    if (loading) {
      return (
        <Atmos style={styles.center}>
          <ActivityIndicator color={t.gold} />
        </Atmos>
      );
    }

    const visible = reaches;

    const renderReachCard = (item: ReachRow) => (
      <Pressable key={item.id} onPress={() => openReach(item)} onLongPress={() => openReachMenu(item)}>
        <GlassCard style={styles.card}>
        <View style={styles.cardTitleRow}>
          <Pressable onPress={() => toggleActive(item)} hitSlop={8} style={[styles.dot, item.is_active !== false ? styles.dotActive : styles.dotInactive]} />
          <Text style={styles.cardTitle}>{item.project_title || 'Untitled Reach'}</Text>
        </View>
        <Text style={styles.cardStatus}>{statusLabel(item)}</Text>
        </GlassCard>
      </Pressable>
    );

    return (
      <Atmos style={styles.root}>
        <View style={styles.helpRow}>
          <ScreenHelp help={sofiaHelp} />
        </View>
        <View style={styles.inputRow}>
          <Field
            style={styles.inputWrap}
            inputStyle={styles.input}
            placeholder="New REACH project title…"
            value={newTitle}
            onChangeText={setNewTitle}
            onSubmitEditing={createReach}
            returnKeyType="done"
          />
          <GoldButton label="Add" onPress={createReach} style={[styles.addButton, busy && styles.addButtonBusy]} />
        </View>
        {error ? <Text style={styles.error}>{error}</Text> : null}
        <ScrollView contentContainerStyle={styles.list}>
          {visible.length === 0 ? (
            <Text style={styles.empty}>No REACH projects yet. Start one above.</Text>
          ) : (
            visible.map(renderReachCard)
          )}
        </ScrollView>
        <Text style={styles.hint}>Tap to open · tap the dot to toggle active · long-press for active/delete</Text>
      </Atmos>
    );
  }

  // -------------------------------------------------------------------------
  // Render: DETAIL screen
  // -------------------------------------------------------------------------

  if (detailLoading || !row) {
    return (
      <Atmos style={styles.center}>
        <ActivityIndicator color={t.gold} />
      </Atmos>
    );
  }

  const basicSteps = row.basic_steps.length ? row.basic_steps : ensureDefaultBasicSteps();
  const hasTaskLink = !!row.sync_list_id;

  return (
    <Atmos>
    <ScrollView contentContainerStyle={styles.detailScroll}>
      <View style={styles.helpRow}>
        <ScreenHelp help={sofiaHelp} />
      </View>
      {/* Top bar: Back + title */}
      <View style={styles.detailTopBar}>
        <Pressable onPress={goBack} style={styles.backButton} hitSlop={8}>
          <Text style={styles.backButtonText}>‹ Back</Text>
        </Pressable>
        <Field
          style={styles.titleWrap}
          inputStyle={styles.titleInput}
          value={row.project_title}
          onChangeText={(v) => setField('project_title', v)}
          placeholder="New REACH Project"
        />
        {isSaving ? <ActivityIndicator size="small" color={t.gold} /> : null}
      </View>

      {/* Basic view table (one row per step, dynamic labels from row.steps) */}
      <GlassCard style={styles.panel}>
        {row.steps.map((step) => (
          <View key={step.id} style={styles.basicRow}>
            <View style={styles.basicRowHeader}>
              <Text style={styles.basicRowLabel}>{step.label}</Text>
            </View>
            <Text style={styles.basicRowPrompt}>{DEFAULT_STEP_PROMPTS[step.label] ?? ''}</Text>
            <Field
              inputStyle={[styles.fieldInput, styles.fieldInputMultiline]}
              value={step.what}
              onChangeText={(v) => updateStep(step.id, { what: v })}
              placeholder="..."
              multiline
            />
          </View>
        ))}
      </GlassCard>

      {/* Steps table */}
      <GlassCard style={styles.panel}>
        <View style={styles.labelRow}>
          <Text style={styles.fieldLabel}>Steps</Text>
          <SmallButton label="+ Add Step" onPress={addBasicStep} />
        </View>
        {basicSteps.map((step, i) => (
          <View key={step.id} style={styles.basicStepRow}>
            <Pressable
              onPress={() => updateBasicStep(step.id, { checked: !step.checked }, basicSteps)}
              style={[styles.checkbox, step.checked ? styles.checkboxChecked : null]}
            >
              {step.checked ? <Text style={styles.checkboxMark}>✓</Text> : null}
            </Pressable>
            <Text style={styles.indexLabel}>{i + 1}.</Text>
            <Field
              style={styles.basicStepWrap}
              inputStyle={styles.basicStepInput}
              value={step.text}
              onChangeText={(v) => updateBasicStep(step.id, { text: v }, basicSteps)}
              placeholder="..."
            />
            {basicSteps.length > 1 ? <RemoveMark onPress={() => removeBasicStep(step.id, basicSteps)} /> : null}
          </View>
        ))}
      </GlassCard>

      {/* Task section — see file header EXCLUSIONS #1: linking/embedding is a
          separate Tasks feature; sync_* fields still round-trip untouched. */}
      <View style={styles.subHeaderBar}>
        <Text style={styles.subHeaderText}>Task</Text>
      </View>
      <GlassCard style={styles.subPanel}>
        <Text style={styles.mutedText}>
          {hasTaskLink
            ? 'A task list is linked to this REACH project. Manage it from the web app or the Tasks tool.'
            : 'No task list linked yet. Link one from the web app to sync tasks here.'}
        </Text>
      </GlassCard>
    </ScrollView>
    </Atmos>
  );
}

// ---------------------------------------------------------------------------
// Styles (matches TkneTool.tsx conventions)
// ---------------------------------------------------------------------------

const useStyles = makeThemedStyles(({ t }) =>
  StyleSheet.create({
    root: { flex: 1, padding: 16 },
    center: { flex: 1, alignItems: 'center', justifyContent: 'center' },
    helpRow: { flexDirection: 'row', justifyContent: 'flex-end', marginBottom: -4 },
    inputRow: { flexDirection: 'row', gap: 8, marginBottom: 10 },
    inputWrap: { flex: 1 },
    input: { fontSize: 15 },
    addButton: { paddingHorizontal: 18 },
    addButtonBusy: { opacity: 0.6 },
    error: { color: t.lapsed, marginBottom: 8, fontSize: 13 },
    list: { gap: 10, paddingBottom: 8 },
    empty: { color: t.ivorySoft, fontStyle: 'italic', textAlign: 'center', marginTop: 24 },
    hint: { fontSize: 11, color: t.ivoryFaint, textAlign: 'center', marginTop: 6 },

    card: {
      padding: 14,
      gap: 4,
    },
    cardTitleRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
    cardTitle: { fontSize: 15, color: t.ivory, flex: 1, fontFamily: DISPLAY_FONT, letterSpacing: 1.5, textTransform: 'uppercase' },
    cardStatus: { fontSize: 12, color: t.ivorySoft },
    dot: { width: 12, height: 12, borderRadius: 6 },
    dotActive: { backgroundColor: t.onPace },
    dotInactive: { backgroundColor: t.ivoryFaint },

    detailScroll: { padding: 16, paddingBottom: 40, gap: 12 },
    detailTopBar: { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' },
    backButton: { paddingVertical: 4, paddingRight: 6 },
    backButtonText: { color: t.goldBright, fontWeight: '700', fontSize: 15 },
    titleWrap: { flex: 1 },
    titleInput: { fontSize: 18, fontWeight: '700', fontFamily: DISPLAY_FONT, letterSpacing: 1 },

    panel: {
      padding: 14,
      gap: 10,
    },
    subPanel: {
      padding: 14,
      gap: 8,
    },

    subHeaderBar: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: 4,
      marginTop: 8,
    },
    subHeaderText: { color: t.ivory, fontSize: 14, fontFamily: DISPLAY_FONT, letterSpacing: 2.5, textTransform: 'uppercase' },
    mutedText: { color: t.ivorySoft, fontSize: 13, fontStyle: 'italic' },

    labelRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
    fieldLabel: { fontSize: 10.5, fontWeight: '800', color: t.gold, letterSpacing: 2.2, textTransform: 'uppercase' },
    fieldInput: { paddingHorizontal: 10, paddingVertical: 8, fontSize: 14 },
    fieldInputMultiline: { minHeight: 70, textAlignVertical: 'top' },

    basicRow: { borderBottomWidth: 1, borderBottomColor: t.lineFaint, paddingBottom: 10, marginBottom: 4, gap: 6 },
    basicRowHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
    basicRowLabel: { fontFamily: DISPLAY_FONT, fontSize: 13, letterSpacing: 2, color: t.goldBright, textTransform: 'uppercase' },
    basicRowPrompt: { fontSize: 12, fontStyle: 'italic', color: t.ivorySoft },
    basicStepRow: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 6 },
    basicStepWrap: { flex: 1 },
    basicStepInput: { fontSize: 14, paddingHorizontal: 10, paddingVertical: 7 },

    indexLabel: { fontSize: 12, fontWeight: '700', color: t.ivoryFaint },

    checkbox: {
      width: 22,
      height: 22,
      borderRadius: 6,
      borderWidth: 1.5,
      borderColor: t.gold,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'transparent',
    },
    checkboxChecked: { backgroundColor: t.gold, borderColor: t.gold },
    checkboxMark: { color: t.onGold, fontSize: 13, fontWeight: '900' },

    removeButton: { paddingHorizontal: 6, paddingVertical: 2 },
    removeMark: { fontSize: 18, color: t.lapsed, fontWeight: '700' },

    smallButton: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: radii.pill, borderWidth: 1, borderColor: t.navyLine },
    smallButtonText: { fontSize: 12, fontWeight: '700', color: t.goldBright },
  }),
);
